/**
 * ContactDialog — a shared "Trouble registering?" link + modal contact form.
 *
 * Rendered on the login/registration screens of every portal. A user who
 * cannot get registered or logged in clicks the trigger link, fills in their
 * details and the issue they're facing, and submits. The parent portal wires
 * the actual transport via the required async ``onSubmit`` prop (each app POSTs
 * to its own ``/api/contact`` endpoint, which emails info@bidconvey.com).
 *
 * Transport-agnostic by design: this component owns only the UI, validation,
 * and success/error states — never a hardcoded URL or axios instance — so it
 * works identically across the conveyancer, seller, and buyer portals despite
 * their different API clients.
 */

import { useEffect, useRef, useState } from 'react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY = { name: '', email: '', company_name: '', issue: '' };

export function ContactDialog({
  onSubmit,
  triggerLabel = 'Trouble registering?',
  title = 'Trouble registering?',
  subtitle = "Tell us what's going wrong and our team will get back to you.",
  triggerClassName = 'text-sm text-blue-700 hover:underline',
  buttonClassName = 'bg-blue-700 hover:bg-blue-800',
}) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState('');
  const firstFieldRef = useRef(null);

  // Reset to a clean form each time the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setValues(EMPTY);
      setErrors({});
      setSent(false);
      setSendError('');
      setSubmitting(false);
    }
  }, [open]);

  // Close on Escape while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const setField = (field) => (e) => {
    const value = e.target.value;
    setValues((v) => ({ ...v, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = 'Please enter your name.';
    if (!values.email.trim()) {
      next.email = 'Please enter your email.';
    } else if (!EMAIL_RE.test(values.email.trim())) {
      next.email = 'Please enter a valid email address.';
    }
    if (!values.issue.trim()) {
      next.issue = 'Please describe the issue.';
    } else if (values.issue.trim().length < 10) {
      next.issue = 'Please give us a little more detail (at least 10 characters).';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSendError('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit({
        name: values.name.trim(),
        email: values.email.trim(),
        company_name: values.company_name.trim(),
        issue: values.issue.trim(),
      });
      setSent(true);
    } catch (err) {
      setSendError(
        (err && (err.message || String(err))) ||
          'Something went wrong sending your message. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const fieldBase =
    'w-full rounded-md border px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500';
  const fieldError = 'border-red-400 focus:ring-red-400';
  const fieldOk = 'border-gray-300';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={triggerClassName}
        data-testid="trouble-registering-link"
      >
        {triggerLabel}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                {!sent && (
                  <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="ml-3 shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {sent ? (
              <div className="px-5 py-8 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="2.5">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </div>
                <p className="text-base font-medium text-gray-900">Message sent</p>
                <p className="mt-1 text-sm text-gray-500">
                  Thank you — our team will be in touch shortly.
                </p>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className={`mt-5 rounded-md px-4 py-2 text-sm font-semibold text-white ${buttonClassName}`}
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 px-5 py-5" noValidate>
                {sendError && (
                  <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {sendError}
                  </div>
                )}

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Name
                  </label>
                  <input
                    ref={firstFieldRef}
                    type="text"
                    value={values.name}
                    onChange={setField('name')}
                    className={`${fieldBase} ${errors.name ? fieldError : fieldOk}`}
                    placeholder="Your full name"
                    data-testid="contact-name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    value={values.email}
                    onChange={setField('email')}
                    className={`${fieldBase} ${errors.email ? fieldError : fieldOk}`}
                    placeholder="you@example.com"
                    data-testid="contact-email"
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Company / Firm{' '}
                    <span className="font-normal text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={values.company_name}
                    onChange={setField('company_name')}
                    className={`${fieldBase} ${fieldOk}`}
                    placeholder="Company or firm name"
                    data-testid="contact-company"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    What's the issue?
                  </label>
                  <textarea
                    rows={4}
                    value={values.issue}
                    onChange={setField('issue')}
                    className={`${fieldBase} resize-none ${errors.issue ? fieldError : fieldOk}`}
                    placeholder="Tell us what's going wrong…"
                    data-testid="contact-issue"
                  />
                  {errors.issue && (
                    <p className="mt-1 text-xs text-red-600">{errors.issue}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className={`flex w-full items-center justify-center rounded-md px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60 ${buttonClassName}`}
                  data-testid="contact-submit"
                >
                  {submitting ? 'Sending…' : 'Send message'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default ContactDialog;
